import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, BehaviorSubject, distinctUntilChanged } from 'rxjs';
import { IAccommodationOfferShort } from '../interfaces/interface';
import { environment } from 'src/environments/environment.local';

@Injectable({ providedIn: 'root' })
export class AccomodationService {
  private http = inject(HttpClient);

  private reload$ = new BehaviorSubject<void>(undefined);

  //сaseToken
  private caseToken: string | null = null;

  setCaseToken(token: string): void {
    this.caseToken = token;
  }

  getCaseToken(): string | null {
    return this.caseToken;
  }

  getOffers(): Observable<IAccommodationOfferShort[]> {
    const url = `${environment.apiBase}offers/${this.caseToken}`;
    return this.http
      .get<any>(url)
      .pipe(
        map(dto =>
          (dto?.offers ?? []).map((raw: any) => this.normalizeOffer(raw))
        )
      );
  }

  refreshOffers(): void {
    this.reload$.next();
  }

  getOfferById(id: number): Observable<IAccommodationOfferShort | null> {
    return this.getOffers().pipe(
      map(list => list.find(o => String(o.id) === String(id)) ?? null),
      distinctUntilChanged()
    );
  }

  private normalizeOffer(raw: any): IAccommodationOfferShort {
    return {
      id: this.toNumber(raw.id),
      title: String(raw.title ?? ''),
      address: String(raw.address ?? ''),
      description: String(raw.description ?? ''),
      distanceFromHome: String(raw.distanceFromHome ?? ''),
      photos: this.parsePhotos(raw.photos),
      features: this.parseFeatures(raw.features),
      first_name: String(raw.first_name ?? ''),
      surname: String(raw.surname ?? ''),
    };
  }

  private toNumber(v: any): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }

  private parseFeatures(features: any): string[] {
    if (Array.isArray(features))
      return features.map(x => String(x).trim()).filter(Boolean);
    if (typeof features === 'string') {
      try {
        const arr = JSON.parse(features);
        if (Array.isArray(arr))
          return arr.map(x => String(x).trim()).filter(Boolean);
      } catch {
        /* fallthrough */
      }
      return features
        .split(',')
        .map(s => s.trim().replace(/^"|"$/g, ''))
        .filter(Boolean);
    }
    return [];
  }

  private parsePhotos(photos: any): string[] {
    try {
      if (Array.isArray(photos)) {
        return photos.map((x: any) => String(x).trim()).filter(this.isHttpUrl);
      }

      if (typeof photos === 'string') {
        const parsed = JSON.parse(photos); // 👈 важно!
        if (Array.isArray(parsed)) {
          return parsed
            .map((x: any) => String(x).trim())
            .filter(this.isHttpUrl);
        }
      }
    } catch (e) {
      console.warn('Error parsing photos', e);
    }

    return [];
  }

  private isHttpUrl = (s: string) => /^https?:\/\//i.test(s);

  private BOOKED_KEY = 'accommodationBooked';
  isBooked(): boolean {
    return localStorage.getItem(this.BOOKED_KEY) === 'true';
  }

  getBookedId(): number | null {
    const val = localStorage.getItem(this.BOOKED_KEY);
    return val ? Number(val) : null;
  }

  confirmOffer(offerId: string): Observable<any> {
    const body = { id: String(offerId) };

    const url = `${environment.apiBase}confirm/${this.caseToken}`;
    return this.http.post(url, body, {
      headers: { 'Content-Type': 'application/json' },
    });
  }
}
